/**
 * Client cache version check. When server reports a newer clientDataVersion and
 * cacheVersionEnabled is true, clear character/campaign caches so clients re-sync from server.
 * Does not clear auth_token or auth_user.
 */

import { apiClient } from '@/api/client';
import * as userDataStore from '@/utils/userDataStore';
import { offlineCache } from '@/utils/offlineCache';

const VERSION_KEY = 'clientDataVersion';

/**
 * Call once on app load (e.g. from AuthProvider). Fetches /api/meta; if cacheVersionEnabled
 * and version differs from localStorage, clears character/campaign IndexedDB and related
 * localStorage keys, then stores the new version.
 */
export async function ensureCacheVersion() {
  try {
    const meta = await apiClient.getMeta();
    if (meta.cacheVersionEnabled === false) {
      return;
    }
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(VERSION_KEY) : null;
    const storedVersion = stored !== null && stored !== '' ? Number(stored) : 0;
    if (Number(meta.clientDataVersion) === storedVersion) {
      return;
    }
    await userDataStore.replaceAllCharacters([]);
    await userDataStore.replaceAllCampaigns([]);
    try {
      await offlineCache.clearCache('characters');
      await offlineCache.clearCache('campaigns');
    } catch {
      // non-fatal: mirrors must not survive when userDataStore was wiped
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('characters_migrated');
      localStorage.setItem(VERSION_KEY, String(meta.clientDataVersion));
    }
  } catch {
    // Safe to ignore: getMeta() already returns default on error; if something else fails, skip wipe
  }
}
